import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Formik, Form } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import {
  isPasswordResetThrottledError,
  showThrottledResetToast,
  useAuth,
} from "@/core/auth";

const signupSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

export default function Signup() {
  const { forgotPassword, openResetCodeEntry } = useAuth();
  const navigate = useNavigate();

  return (
    <Stack
      direction="row"
      align="center"
      justify="center"
      fullWidth
      className="min-h-[calc(100vh-67px)] overflow-hidden p-6"
    >
      <Card className="w-full max-w-md p-6 pt-8">
        <Formik
          initialValues={{ email: "" }}
          validationSchema={signupSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await forgotPassword(values);
              toast.success("Verification code sent");
            } catch (error) {
              if (isPasswordResetThrottledError(error)) {
                showThrottledResetToast(error.message, () =>
                  openResetCodeEntry(values.email),
                );
                return;
              }
              toast.error("Unable to process signup", {
                description:
                  "Please make sure your organization admin has created your account, and you're using the same email as in your welcome email.",
              });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ getFieldProps, isSubmitting, isValid }) => (
            <Form>
              <Stack gap="lg" fullWidth>
                <Typography variant="subtitleW1" className="mt-4">
                  Sign up for your account
                </Typography>

                <Stack direction="row" align="center" gap="xs">
                  <Typography variant="bodyW1" textColor="secondary">
                    We'll send you a code to set your password if an
                    administrator has created your account.
                  </Typography>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InformationCircleIcon className="h-5 w-5 min-w-5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent maxWidth="lg">
                      Make sure to use the same email that your organization
                      admin used when creating your account. This is likely the
                      same email you use to sign in to your work account.
                    </TooltipContent>
                  </Tooltip>
                </Stack>

                <FormInput
                  id="email"
                  type="email"
                  label="Email"
                  placeholder="Enter your email"
                  {...getFieldProps("email")}
                  fullWidth
                />

                <Stack fullWidth gap="sm">
                  <Button
                    type="submit"
                    size="lg"
                    fullWidth
                    disabled={!isValid}
                    loading={isSubmitting}
                  >
                    Continue
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="text"
                    fullWidth
                    className="[&_svg]:size-5"
                    leftIcon={<ArrowLeftIcon />}
                    onClick={() => navigate("/login")}
                  >
                    Back to sign in
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
